import '~/styles/music-classical.scss'; // This must be imported first to ensure base styles are imported first
import App from '~/App.svelte';
import { bootstrap } from '~/bootstrap';
import { registerActionHandlers } from '~/jet/action-handlers';
import { PrefetchedIntents } from '@amp/web-apps-common/src/jet/prefetched-intents';
import type { Page } from '~/jet/models';
import {
    CompositeLoggerFactory,
    ConsoleLoggerFactory,
    DeferredLoggerFactory,
    setContext,
} from '@amp/web-apps-logger';
import { setupClickAction } from '~/actions/click-action';
import { setupItemSelector } from '~/stores/item-selector/browser-only';

import { setHTMLAttributes } from '@amp/web-apps-localization';
import { ERROR_KIT_CONFIG } from '~/config/errorkit';
import {
    ErrorKitLoggerFactory,
    setupErrorKit,
} from '@amp/web-apps-logger/src/errorkit';
import {
    handleCampaignAndAffiliateUrls,
    MUSIC_AFFILIATIONS_ENDPOINT,
} from '@amp/affiliate-util';
import { PERF_KIT_CONFIG } from '~/config/metrics';
import { setPageSpeedContext, setupBrowserPerfkit } from '~/utils/perfkit';
import { DEFAULT_ORIGIN } from '~/utils/launch-utils';
import { Playback } from '~/utils/playback/browser-only';
import { Commerce } from '~/utils/commerce/browser-only';
import { ContextualMenu } from './utils/contextual-menu/browser-only';
import { getCookie } from '@amp/web-app-components/src/utils/cookie';
import { fullScreenPlayerState } from '~/stores/full-screen-player';
import { Locale } from '~/utils/locale';
import { setupRuntimeFeatures } from '~/utils/features/runtime';

export async function startApplication() {
    const onyxFeatures = await setupRuntimeFeatures(
        new DeferredLoggerFactory(() => logger),
    );
    const consoleLogger = new ConsoleLoggerFactory();
    const errorKit = setupErrorKit(ERROR_KIT_CONFIG, consoleLogger);
    const logger = new CompositeLoggerFactory([
        consoleLogger,
        new ErrorKitLoggerFactory(errorKit),
        ...(onyxFeatures ? [onyxFeatures.recordingLogger] : []),
    ]);

    let url = window.location.href;

    // Handle affiliate redirects.
    try {
        const { pathname, search } = new URL(url);
        // The targetUrl needs to be normalized to classical.music.apple.com as
        // that is what the service endpoint expects.
        const targetUrl = `${DEFAULT_ORIGIN}${pathname}${search}`;
        // TODO rdar://120479654 (Andorra Web:  Endpoints for affiliate campaigns.)
        const redirectUrl = await handleCampaignAndAffiliateUrls(targetUrl, {
            ignoreMarketing: true,
            endpoint: MUSIC_AFFILIATIONS_ENDPOINT,
        });

        if (redirectUrl) {
            url = redirectUrl;
        }
    } catch (e) {
        // No-op to handle most cases when
        // `handleCampaignAndAffiliateUrls()` rejects
        // because no campaign tokens are present in the URL.
    }

    const getUserInfo = (): { storefront: string; mut: string } | undefined => {
        if (getCookie('itua') && getCookie('media-user-token')) {
            return {
                storefront: Locale.normalize({
                    storefront: getCookie('itua'),
                    language: null,
                }).storefront,
                mut: getCookie('media-user-token'),
            };
        }
    };

    // Initialize Jet, and get starting state.
    const { context, jet, initialAction, storefront, language, locale, i18n } =
        await bootstrap(
            logger,
            url,
            window.fetch.bind(window),
            {
                getUserInfo,
            },
            PrefetchedIntents.fromDom(logger),
            getUserInfo()?.storefront, // This has been added because Library Routes in classical do not have Storefront in their Routes & hence correct storefront is needed in Browser to make API calls with correct SF
        );

    setContext(context, logger);
    setupClickAction(jet, logger);

    // Add lang + dir tag to HTML node
    setHTMLAttributes(language);

    const perfkit = setupBrowserPerfkit(PERF_KIT_CONFIG, logger);
    setPageSpeedContext(context, perfkit, logger);

    // load MusicKit
    const playback = Playback.load(context, logger, jet, storefront, language);

    // Gloabl store for Item Selector to be setup
    setupItemSelector(context);

    // Setting Up Commerce Manager & Storing its Instance in a Context for future use
    const commerce = Commerce.setup(context, logger, playback, jet, {
        language,
        storefront,
    });

    // Setting up Contextual Menu
    const contextualMenu = ContextualMenu.setup(context, logger, i18n);

    // Using a container element to avoid svelte hydration
    // "clean up" from removing tags that have
    // been add to the <body> tag in our HTML file.
    const container = document.querySelector('.body-container');

    // TODO: rdar://78109641 (`context` property missing from svelte component type)
    const app = new App({
        target: container,
        context,
        hydrate: true,
    } as any);

    // Initialize action-handlers.
    registerActionHandlers({
        jet,
        logger,
        updateApp: (props: {
            page?: Promise<Page>;
            isFirstPage: boolean;
        }): void => app.$set(props),
        perfkit,
        playback,
        commerce,
        contextualMenu,
        fullScreenPlayerState,
    });

    if (initialAction) {
        // TODO: rdar://73165545 (Error Handling Across App): handle throw
        await jet.perform(initialAction);
    } else {
        // A null initialAction indicates a 404
        app.$set({
            page: Promise.reject(new Error('404')),
            isFirstPage: true,
        });
    }
}

// If we export default here, this will run during tests when we do
// `import { startApplication } from '~/browser';`. To avoid this, we guard using the
// presence of an ENV var only set by Vitest.

// This is covered by acceptance tests
if (!import.meta.env?.VITEST) {
    startApplication();
}
